'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  DEFAULT_MANUAL_STAGE_LAYOUT,
  readManualLayout,
  writeManualLayout,
  type ManualStageLayout,
} from '@/lib/manual-layout';

/**
 * The operator's stage layout, remembered between sessions.
 *
 * Read AFTER mount, never during render, for the same reason the map style is
 * (`droneMapReducer.getPersistedMapStyle`): the server renders without any
 * `localStorage`, so seeding state from it during the first render is a
 * hydration mismatch. The first paint is therefore always the default MAP
 * layout, and a stored preference replaces it on the next tick — which is
 * correct anyway, since manual flight is never live during that first frame.
 *
 * Changing the layout mid-gesture would carry a vector across a change of
 * scale, so `setLayout` runs `onBeforeChange` FIRST — the COP passes the
 * controller's `hover` there, which cancels the drag on the wire before
 * anything is re-hosted.
 */
export function useManualStageLayout({
  operator = null,
  onBeforeChange,
}: {
  /** Stable operator identity (email today). A shared console must not blend two operators' choices. */
  operator?: string | null;
  /** Run before the layout changes. Must be synchronous — a stop is not something to schedule. */
  onBeforeChange?: () => void;
} = {}): {
  layout: ManualStageLayout;
  setLayout: (next: ManualStageLayout) => void;
  /** False when the choice could not be persisted, so the caller can say so rather than lie. */
  persisted: boolean;
} {
  const [layout, setLayoutState] = useState<ManualStageLayout>(DEFAULT_MANUAL_STAGE_LAYOUT);
  const [persisted, setPersisted] = useState(true);

  // The current layout and the cancel callback are both read from inside an
  // event handler, so they go through refs: a stale closure here would either
  // skip the cancel or compare against a layout the operator already left.
  const layoutRef = useRef(layout);
  layoutRef.current = layout;
  const onBeforeChangeRef = useRef(onBeforeChange);
  onBeforeChangeRef.current = onBeforeChange;

  useEffect(() => {
    const stored = readManualLayout(operator);
    layoutRef.current = stored;
    setLayoutState(stored);
  }, [operator]);

  const setLayout = useCallback(
    (next: ManualStageLayout) => {
      if (layoutRef.current === next) return;
      // Before, not after: the aircraft must be told to stop while the old
      // scale is still the one in force.
      onBeforeChangeRef.current?.();
      layoutRef.current = next;
      setPersisted(writeManualLayout(next, operator));
      setLayoutState(next);
    },
    [operator],
  );

  return { layout, setLayout, persisted };
}
